import { Injectable, inject } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { unionBy } from 'lodash';
import { HttpClient } from '@angular/common/http';

import { EntityRef, GlobalHotel } from '@core/models';
import { catchError, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';
import { environment } from '@env/environment';
import { PageContent } from '@shared/models';
import { unwrapApiData } from '@core/utils/api-response';

interface Region {
  id: string;
  regionName: string;
  description?: string;
}

interface Owner {
  id: string;
  groupName: string;
  description?: string;
}

interface FilterData {
  enterprises: EntityRef[];
  brands: EntityRef[];
  hotels: GlobalHotel[];
  regions: Region[];
  owners: Owner[];
}

const store = createStore(
  { name: 'globalProperty' },
  withProps<FilterData>({ enterprises: [], brands: [], hotels: [], regions: [], owners: [] })
);

@Injectable({
  providedIn: 'root',
})
export class GlobalPropertyRepository {
  private readonly http = inject(HttpClient);

  private readonly enterpriseCache$ = new Map<string, Observable<any>>();
  private readonly brandCache$ = new Map<string, Observable<any>>();
  private readonly hotelCache$ = new Map<string, Observable<any>>();

  readonly enterprises$: Observable<EntityRef[]> = store.pipe(select((state) => state.enterprises));
  readonly brands$: Observable<EntityRef[]> = store.pipe(select((state) => state.brands));
  readonly hotels$: Observable<GlobalHotel[]> = store.pipe(select((state) => state.hotels));
  readonly regions$: Observable<Region[]> = store.pipe(select((state) => state.regions));
  readonly owners$: Observable<Owner[]> = store.pipe(select((state) => state.owners));

  setEnterprise = (enterprises: EntityRef[]) => {
    // const inStore = store.getValue().enterprises;
    // const uniqueEnterprises = enterprises.length ? unionBy([...inStore, ...enterprises], 'id') : [];
    // store.update((state) => ({ ...state, enterprises: uniqueEnterprises }));
    store.update((state) => ({ ...state, enterprises }));
  };

  setBrands = (brands: EntityRef[]) => {
    // Replace brands instead of merging (shows only filtered results)
    store.update((state) => ({ ...state, brands }));
  };

  setHotels = (hotels: GlobalHotel[]) => {
    // Replace hotels instead of merging (shows only filtered results)
    store.update((state) => ({ ...state, hotels }));
  };

  setRegions = (regions: Region[]) => {
    const inStore = store.getValue().regions;
    const uniqueRegions = regions.length ? unionBy([...inStore, ...regions], 'id') : [];
    store.update((state) => ({ ...state, regions: uniqueRegions }));
  };

  setOwners = (owners: Owner[]) => {
    // Replace owners instead of merging (shows only filtered results)
    store.update((state) => ({ ...state, owners }));
  };

  fetchEnterprises = (ownerIds: string[] = [], regionIds: string[] = [], search?: string) => {
    const cacheKey = `enterprises-${ownerIds.join(',')}-${regionIds.join(',')}-${search || ''}`;

    if (this.enterpriseCache$.has(cacheKey)) {
      return this.enterpriseCache$.get(cacheKey)!;
    }

    const payload: any = { is_active: true };
    if (ownerIds.length) payload.ownership_groups = ownerIds;
    if (regionIds.length) payload.regions = regionIds;
    if (search) payload.search = search;

    const enterprises$ = this.http.post<any>(`${environment.newBaseUrl}/enterprises`, payload).pipe(
      map((response) => {
        const data = response?.data || response || [];
        return (Array.isArray(data) ? data : []).map((e: any) => ({ id: e.id, name: e.name }));
      }),
      tap((enterprises) => {
        this.setEnterprise(enterprises);
      })
    );
    this.enterpriseCache$.set(cacheKey, enterprises$);
    return enterprises$;
  };

  fetchBrands = (
    enterpriseIds: string[],
    ownerIds: string[] = [],
    regionIds: string[] = [],
    search?: string
  ) => {
    const cacheKey = `brands-${enterpriseIds.join(',')}-${ownerIds.join(',')}-${regionIds.join(
      ','
    )}-${search || ''}`;

    // Check if already cached
    if (this.brandCache$.has(cacheKey)) {
      return this.brandCache$.get(cacheKey)!;
    }

    const payload: any = {};
    if (enterpriseIds.length) payload.enterprises = enterpriseIds;
    if (ownerIds.length) payload.ownership_groups = ownerIds;
    if (regionIds.length) payload.regions = regionIds;
    if (search) payload.search = search;

    const brands$ = this.http.post<any>(`${environment.newBaseUrl}/brands`, payload).pipe(
      map((response) => {
        const data = response?.data || response || [];
        return (Array.isArray(data) ? data : []).map((b: any) => ({ id: b.id, name: b.name }));
      }),
      tap((brands) => {
        this.setBrands(brands);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => this.brandCache$.delete(cacheKey))
    );

    this.brandCache$.set(cacheKey, brands$);
    return brands$;
  };

  fetchHotels = (
    brandIds: string[],
    enterpriseIds: string[],
    ownerIds: string[] = [],
    regionIds: string[] = [],
    search?: string
  ) => {
    const cacheKey = `hotels-${brandIds.join(',')}-${enterpriseIds.join(',')}-${ownerIds.join(
      ','
    )}-${regionIds.join(',')}-${search || ''}`;

    if (this.hotelCache$.has(cacheKey)) {
      return this.hotelCache$.get(cacheKey)!;
    }

    const payload: any = { is_active: true };
    if (brandIds.length) payload.brands = brandIds;
    if (enterpriseIds.length) payload.enterprises = enterpriseIds;
    if (ownerIds.length) payload.ownership_groups = ownerIds;
    if (regionIds.length) payload.regions = regionIds;
    if (search) payload.search = search;

    const hotels$ = this.http.post<any>(`${environment.newBaseUrl}/hotels`, payload).pipe(
      map((response) => {
        const data = response?.data || response || [];
        return (Array.isArray(data) ? data : []).map(
          (h: any): GlobalHotel => ({
            id: h.id,
            name: h.name,
            code: h.code,
            displayName: h.display_name || `${h.code} - ${h.name}`,
            brandId: h.brand_id ?? h.brand?.id,
          })
        );
      }),
      tap((hotels) => {
        this.setHotels(hotels);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => this.hotelCache$.delete(cacheKey)),
      catchError(() => of([]))
    );

    this.hotelCache$.set(cacheKey, hotels$);
    return hotels$;
  };

  clearBrandCache() {
    this.brandCache$.clear();
  }

  clearHotelCache() {
    this.hotelCache$.clear();
  }

  fetchRegions = (search?: string) => {
    const payload: any = {};
    if (search) payload.search = search;

    return this.http.post<any>(`${environment.newBaseUrl}/regions`, payload).pipe(
      map((response) => {
        const data = response?.data || response || [];
        return (Array.isArray(data) ? data : []).map((r: any) => ({
          id: r.id,
          regionName: r.name,
          description: r.description,
        }));
      }),
      tap((regions: Region[]) => {
        this.setRegions(regions);
      }),
      catchError(() => of([]))
    );
  };

  fetchOwners = (search?: string) => {
    const payload: any = { role_type: 'ownership_group' };

    // Add enterprise filter if provided
    // if (enterpriseIds.length > 0) {
    //   payload.enterprises = enterpriseIds;
    // }

    // Only include search if provided
    if (search) {
      payload.search = search;
    }

    return this.http.post<any>(`${environment.newBaseUrl}/roles/accessible`, payload).pipe(
      map((response) => {
        const data = unwrapApiData(response) || [];
        return (Array.isArray(data) ? data : []).map((item: any) => ({
          id: item.id,
          groupName: item.name,
          description: item.description,
        }));
      }),
      tap((owners) => {
        this.setOwners(owners);
      }),
      catchError(() => of([]))
    );
  };
}
